import { useEffect, useMemo, useState } from 'react';

import { createAssociationMembers } from '@src/api/group';
import useGetAssociationLicenseMembers from '@src/hooks/queries/group/useGetAssociationLicenseMembers';
import { Button, Input, Modal, Table, message } from 'antd';
import update from 'immutability-helper';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { WorkBook, read, utils } from 'xlsx';

import { closeModal } from '@reduce/modals';

import getIsResponseFalse from '@utils/getIsResponseFalse';
import { trigger } from '@utils/globalEvents';

import type {
  AssociationMembersData,
  AssociationMembersDataSource,
} from '@pages/address-book/AssociationMembers/Table/type';

import FileUpload from '../CreateMembersModal/FileUpload';
import type * as T from './type';

const PAGE_SIZE: number = 10;

function CreateAssociationMultipleMembersModal({
  associationId,
}: T.CreateAssociationMultipleMembersModalProps) {
  const dispatch = useDispatch();

  const [isUpload, setIsUpload] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [filerows, setFilerows] = useState<string[][]>();
  const [selectedMembers, setSelectedMembers] = useState<AssociationMembersDataSource>([]);
  const [notSelectedMembers, setNotSelectedMembers] = useState<AssociationMembersDataSource>([]);
  const [allSelect, setAllSelect] = useState<boolean>(true);
  const [selectCheck, setSelectCheck] = useState<boolean>(false);

  const {
    data: rows,
    isLoading,
    total,
  } = useGetAssociationLicenseMembers({
    q: `licenseNo:${filerows?.join(',')}`,
    page: page,
    limit: PAGE_SIZE,
  });

  const dataSource: AssociationMembersDataSource = useMemo(() => {
    console.log(`licenseNo:${filerows?.join(',')}`);
    if (rows) return (rows as any)?.map((v: any) => ({ ...v, key: v.id }));
  }, [rows]);

  const columns = useMemo(() => {
    return [
      {
        dataIndex: 'name',
        title: '이름',
        // @ts-ignore
        render: name => name,
      },
      {
        dataIndex: 'accountId',
        title: '아이디',
        render: (accountId: any) => accountId,
      },
    ];
  }, [rows]);

  const close = () => dispatch(closeModal('createAssociationMultipleMembers'));

  const select = (data: AssociationMembersData, selected: boolean) => {
    setNotSelectedMembers(prevState => {
      if (!selected) return [...prevState, data];
      const findIndex = prevState.findIndex(v => v.id === data.id);
      if (typeof findIndex === 'undefined') return prevState;

      return update(prevState, {
        $splice: [[findIndex, 1]],
      });
    });
    setSelectedMembers(prevState => {
      if (selected) return [...prevState, data];
      const findIndex = prevState.findIndex(v => v.id === data.id);
      if (typeof findIndex === 'undefined') return prevState;

      return update(prevState, {
        $splice: [[findIndex, 1]],
      });
    });

    if (
      (selectedMembers.length + 1 === filerows?.length && !allSelect) ||
      (notSelectedMembers.length + 1 === filerows?.length && allSelect)
    ) {
      setAllSelect(prev => !prev);
    }
  };

  const selectAll = (selected: boolean, data: AssociationMembersDataSource) => {
    setAllSelect(prev => !prev);
    setSelectedMembers([]);
    setNotSelectedMembers([]);
  };

  const create = async () => {
    if ((allSelect && notSelectedMembers) || (selectedMembers && !allSelect)) {
      let licenseNo = filerows?.map(v => parseInt(v[0]));
      let licenseNos: any = licenseNo?.filter(v =>
        allSelect
          ? !notSelectedMembers.map(member => member.licenseNo).includes(v)
          : selectedMembers.map(member => member.licenseNo).includes(v),
      );

      const response = await createAssociationMembers({
        path: { teamId: associationId },
        data: { licenseNos },
      });

      if (getIsResponseFalse(response)) return false;
    }

    message.success('회원 그룹에 멤버를 추가했습니다.');
    trigger('SUCCESS_CREATE_ASSOCIATION_MEMBERS');
    close();
  };

  const csvFileToArray: (text: string) => string[][] = (text: string) => {
    const csvRows = text
      .slice(text.indexOf('\n') + 1)
      .split('\n')
      .filter(v => v);

    return csvRows.map((row, index) => {
      console.log(row);
      const licenseNo = row;

      const uuid = uuidv4();
      const shortUuid = uuid.split('-')[0];

      return [licenseNo];
    });
  };

  const convertCsv: (file: File) => Promise<string[][]> = (file: File) => {
    return new Promise(resolve => {
      const fileReader = new FileReader();
      fileReader.onload = e => {
        const text = e?.target?.result as string;
        const result = csvFileToArray(text);
        console.log('csv', { result });
        return resolve(result);
      };
      fileReader.readAsText(file);
    });
  };

  const convertXlsx: (file: File) => Promise<
    | false
    | {
        header: string[];
        rows: string[][];
      }
  > = async (file: File) => {
    console.log('xlsx', { file });

    let wb: null | WorkBook = null;

    try {
      wb = read(await file.arrayBuffer());

      const data = utils
        .sheet_to_json<string[]>(wb.Sheets[wb.SheetNames[0]], { header: 1 })
        .filter(v => v + '');

      console.log(data);
      const header = data[0];
      const rows = data.filter((_, i) => i !== 0);
      console.log(header, rows);
      return { header, rows };
    } catch (err) {
      console.log({ err });
      message.info('올바르지 않은 파일입니다.');
      return false;
    }
  };

  const upload = async (files: FileList) => {
    console.log({ files });

    if (!files[0]) return false;

    const file = files[0];
    const ext = file.name.split('.').pop();

    switch (ext) {
      case 'csv': {
        const dataSource: string[][] = await convertCsv(file);
        console.log({ dataSource });
        // return result;

        // handleSelectedMemberKeys(dataSource);
        setFilerows(dataSource);
        return;
      }
      case 'xlsx': {
        const result = await convertXlsx(file);
        if (!result) return;
        console.log('XLSX RESULT', result);
        const { rows } = result;

        console.log('CURRENT DATA', rows);
        // handleSelectedMemberKeys(dataSource);
        setFilerows(rows);
        return;
      }
    }
  };

  const footerBtns = [
    <Button key='cancel' onClick={close}>
      취소
    </Button>,
    <Button key='send' type='primary' onClick={create}>
      추가
    </Button>,
  ];

  return (
    <Modal
      open={true}
      title='그룹 회원 일괄 추가'
      footer={footerBtns}
      width='800px'
      onCancel={close}>
      <FileUpload setIsUpload={setIsUpload} upload={upload} />

      {rows && (
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={isLoading}
          sticky={true}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: dataSource
              .filter(data =>
                allSelect
                  ? !notSelectedMembers.map(member => member.licenseNo).includes(data.licenseNo)
                  : selectedMembers.map(member => member.licenseNo).includes(data.licenseNo),
              )
              .map(data => {
                return data.id;
              }),
            preserveSelectedRowKeys: true,
            onSelect: select,
            onSelectAll: selectAll,
          }}
          pagination={{
            total,
            current: page,
            pageSize: PAGE_SIZE,
            position: ['bottomCenter'],
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      )}
    </Modal>
  );
}

export default CreateAssociationMultipleMembersModal;
